import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

export interface ClientData {
  clientName: string;
  cmsFinNumber: string;
  registrationDate: string;
  employeeHeadCountLimit: number;
  branch: string;
  themeName: string;
  platformEnabled: boolean;
}

export interface ClientDisplayData {
  ClientName: string;
  CompanyCode: string;
  CMSFinNumber: string;
  Registration: string;
  EmployeeHeadcountLimit: string;
  Branch: string;
  status: string;
}

export interface ClientCreationResponse {
  clientName: string;
  cmsFinNumber: string;
  registrationDate: string;
  employeeHeadCountLimit: number;
  branch: string;
  themeName: string;
  platformEnabled: boolean;
  id?: string;
  createdAt?: string;
  [key: string]: any; // Allow additional fields from API
}

export interface Client {
  id: string;
  companyRegNo: string;
  clientName: string;
  cmsFinNumber: string;
  registrationDate: string;
  employeeHeadCountLimit: number;
  branch: string;
  themeName: string;
  platformEnabled: boolean;
  status?: string;
  createdAt?: string;
  [key: string]: any;
}

export interface ClientsListResponse {
  data: Client[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  selfLink?: string;
}

export interface LicenseExpiryData {
  expiryDate: string;
  reason: string;
  comment?: string;
}

export interface Branch {
  code: string;
  name: string;
}

export interface BranchesResponse {
  branches: Branch[];
  selfLink: string;
}

export interface SubBranchesResponse {
  branches: Branch[];
  selfLink: string;
}

export interface Theme {
  code: string;
  name: string;
}

export interface ThemesResponse {
  themes: Theme[];
  selfLink: string;
}

export interface SuspensionReason {
  code: string;
  description: string;
}

export interface SuspensionReasonsResponse {
  reasons: SuspensionReason[];
}

export interface SuspendClientRequest {
  licenceExpiryDate: string;
  reason: string;
  comments?: string;
}

export interface SuspendClientResponse {
  id: string;
  clientName: string;
  cmsFinNumber: string;
  status: string;
  licenceExpiryDate?: string;
  suspensionReason?: string;
  suspensionComments?: string;
  [key: string]: any;
}

export interface ReactivateClientRequest {
  licenceReactivationDate: string;
  comments?: string;
}

export interface ReactivateClientResponse {
  id: string;
  clientName: string;
  cmsFinNumber: string;
  status: string;
  reactivationDate?: string;
  reactivationComments?: string;
  [key: string]: any;
}

export interface UpdateClientRequest {
  clientName: string;
  cmsFinNumber: string;
  registrationDate: string;
  employeeHeadCountLimit: number;
  branch: string;
  themeName: string;
  platformEnabled: boolean;
}

export interface UpdateClientResponse {
  id: string;
  clientName: string;
  cmsFinNumber: string;
  registrationDate: string;
  employeeHeadCountLimit: number;
  branch: string;
  themeName: string;
  platformEnabled: boolean;
  status?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class HomeService {
  // Use environment-aware API base URL
  // In development: uses /api prefix (proxied by dev server)
  // In production: uses direct Azure API Gateway URL
  private readonly apiBaseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) { }

  /**
   * Get list of clients from API
   * @param page - Page number (default: 1)
   * @param pageSize - Number of items per page (default: 10)
   * @param search - Optional free-text search across clientName, finNumber, branchCode, branchName, companyRegNo, etc.
   * @returns Observable with clients list response
   */
  getClients(page: number = 1, pageSize: number = 10, search?: string): Observable<ClientsListResponse> {
    const term = search?.trim();
    const url = term
      ? `${this.apiBaseUrl}/v1/clients/search?q=${encodeURIComponent(term)}&page=${page}&pageSize=${pageSize}`
      : `${this.apiBaseUrl}/v1/clients?page=${page}&pageSize=${pageSize}`;
    console.log('👥 Fetching clients from:', url);
    return this.http.get<ClientsListResponse>(url).pipe(
      tap({
        next: (response) => {
          console.log('✅ Clients API Response:', response);
          console.log('📊 Number of clients:', response.data?.length || 0);
          console.log('📄 Page:', response.page, 'of', response.totalPages);
          console.log('🔢 Total items:', response.totalItems);
        },
        error: (error) => {
          console.error('❌ Clients API Error:', error);
          console.error('Status:', error.status);
          console.error('Status Text:', error.statusText);
          console.error('Error Message:', error.message);
          console.error('Response Body:', error.error);
        }
      })
    );
  }

  /**
   * Get client by ID from API
   * @param clientId - The client ID
   * @returns Observable with client data
   */
  getClientById(clientId: string): Observable<Client> {
    const url = `${this.apiBaseUrl}/v1/clients/${clientId}`;
    console.log('🔍 Fetching client by ID from:', url);
    return this.http.get<Client>(url).pipe(
      tap({
        next: (response) => {
          console.log('✅ Client API Response:', response);
          console.log('📋 Client details:', JSON.stringify(response, null, 2));
        },
        error: (error) => {
          console.error('❌ Client API Error:', error);
          console.error('Status:', error.status);
          console.error('Status Text:', error.statusText);
          console.error('Error Message:', error.message);
          console.error('Response Body:', error.error);
        }
      })
    );
  }

  /**
   * Get list of branches from API
   * @returns Observable with branches response
   */
  getBranches(): Observable<BranchesResponse> {
    console.log('🌿 Fetching branches from:', `${this.apiBaseUrl}/v1/branches`);
    return this.http.get<BranchesResponse>(`${this.apiBaseUrl}/v1/branches`).pipe(
      tap({
        next: (response) => {
          console.log('✅ Branches API Response:', response);
          console.log('📊 Number of branches:', response.branches?.length || 0);
          console.log('🔗 Self Link:', response.selfLink);
        },
        error: (error) => {
          console.error('❌ Branches API Error:', error);
          console.error('Status:', error.status);
          console.error('Status Text:', error.statusText);
          console.error('Error Message:', error.message);
          console.error('Response Body:', error.error);
        }
      })
    );
  }

  /**
   * Get list of sub-branches (conditions) from API
   * @returns Observable with sub-branches response
   */
  getSubBranches(): Observable<SubBranchesResponse> {
    const branchCode = 'OTHER';
    console.log('🌿 Fetching sub-branches from:', `${this.apiBaseUrl}/v1/branches/${branchCode}/sub-branches`);
    return this.http.get<SubBranchesResponse>(`${this.apiBaseUrl}/v1/branches/${branchCode}/sub-branches`).pipe(
      tap({
        next: (response) => {
          console.log('✅ Sub-branches API Response:', response);
          console.log('📊 Number of sub-branches:', response.branches?.length || 0);
          console.log('🔗 Self Link:', response.selfLink);
        },
        error: (error) => {
          console.error('❌ Sub-branches API Error:', error);
          console.error('Status:', error.status);
          console.error('Status Text:', error.statusText);
          console.error('Error Message:', error.message);
          console.error('Response Body:', error.error);
        }
      })
    );
  }

  /**
   * Get list of themes from API
   * @returns Observable with themes response
   */
  getThemes(): Observable<ThemesResponse> {
    // TODO: Replace with actual API call
    // Example: return this.http.get<ThemesResponse>('/v1/themes');

    // Mock data for now
    const mockResponse: ThemesResponse = {
      themes: [
        { code: 'PNP', name: 'PNP' },
        { code: 'DEFAULT', name: 'Default' }
      ],
      selfLink: '/v1/themes'
    };

    // Simulate API call with delay
    return of(mockResponse).pipe(delay(300));
  }

  /**
   * Get list of suspension reasons from API
   * @returns Observable with suspension reasons response
   */
  getSuspensionReasons(): Observable<SuspensionReasonsResponse> {
    console.log('📋 Fetching suspension reasons from:', `${this.apiBaseUrl}/v1/suspension-reasons`);
    return this.http.get<SuspensionReasonsResponse>(`${this.apiBaseUrl}/v1/suspension-reasons`).pipe(
      tap({
        next: (response) => {
          console.log('✅ Suspension Reasons API Response:', response);
          console.log('📊 Number of reasons:', response.reasons?.length || 0);
        },
        error: (error) => {
          console.error('❌ Suspension Reasons API Error:', error);
          console.error('Status:', error.status);
          console.error('Status Text:', error.statusText);
          console.error('Error Message:', error.message);
          console.error('Response Body:', error.error);
        }
      })
    );
  }

  /**
   * Submit new client data
   * @param clientData - The client form data
   * @returns Observable with the API response containing created client data
   */
  submitNewClient(clientData: ClientData): Observable<ClientCreationResponse> {
    console.log('📤 Submitting new client to:', `${this.apiBaseUrl}/v1/clients`);
    console.log('📋 Client data:', clientData);
    return this.http.post<ClientCreationResponse>(`${this.apiBaseUrl}/v1/clients`, clientData).pipe(
      tap({
        next: (response) => {
          console.log('✅ Client submission successful:', response);
          console.log('📋 Response details:', JSON.stringify(response, null, 2));
        },
        error: (error) => {
          console.error('❌ Client submission error:', error);
          console.error('Status:', error.status);
          console.error('Status Text:', error.statusText);
          console.error('Error Message:', error.message);
          console.error('Response Body:', error.error);
        }
      })
    );
  }

  /**
   * Submit license expiry data
   * @param licenseData - The license expiry form data
   * @returns Observable with the submitted license data
   * @deprecated Use suspendClient instead
   */
  submitLicenseExpiry(licenseData: LicenseExpiryData): Observable<LicenseExpiryData> {
    // TODO: Replace with actual API call
    console.log('Submitting license expiry:', licenseData);

    // Simulate API call with delay
    return of(licenseData).pipe(delay(500));
  }

  /**
   * Suspend a client with license expiry information
   * @param clientId - The client ID to suspend
   * @param suspendData - The suspension request data
   * @returns Observable with the suspended client response
   */
  suspendClient(clientId: string, suspendData: SuspendClientRequest): Observable<SuspendClientResponse> {
    const url = `${this.apiBaseUrl}/v1/clients/${clientId}/suspend`;
    console.log('🚫 Suspending client:', clientId);
    console.log('📋 Suspend data:', suspendData);
    console.log('🔗 API URL:', url);
    return this.http.post<SuspendClientResponse>(url, suspendData).pipe(
      tap({
        next: (response) => {
          console.log('✅ Client suspended successfully:', response);
          console.log('�� Response details:', JSON.stringify(response, null, 2));
        },
        error: (error) => {
          console.error('❌ Client suspension error:', error);
          console.error('Status:', error.status);
          console.error('Status Text:', error.statusText);
          console.error('Error Message:', error.message);
          console.error('Response Body:', error.error);
        }
      })
    );
  }

  /**
   * Reactivate a suspended client
   * @param clientId - The client ID to reactivate
   * @param reactivateData - The reactivation request data
   * @returns Observable with the reactivated client response
   */
  reactivateClient(clientId: string, reactivateData: ReactivateClientRequest): Observable<ReactivateClientResponse> {
    const url = `${this.apiBaseUrl}/v1/clients/${clientId}/reactivate`;
    console.log('✅ Reactivating client:', clientId);
    console.log('📋 Reactivate data:', reactivateData);
    console.log('🔗 API URL:', url);
    return this.http.post<ReactivateClientResponse>(url, reactivateData).pipe(
      tap({
        next: (response) => {
          console.log('✅ Client reactivated successfully:', response);
          console.log('📋 Response details:', JSON.stringify(response, null, 2));
        },
        error: (error) => {
          console.error('❌ Client reactivation error:', error);
          console.error('Status:', error.status);
          console.error('Status Text:', error.statusText);
          console.error('Error Message:', error.message);
          console.error('Response Body:', error.error);
        }
      })
    );
  }

  /**
   * Update an existing client
   * @param clientId - The client ID to update
   * @param updateData - The client update data
   * @returns Observable with the updated client response
   */
  updateClient(clientId: string, updateData: UpdateClientRequest): Observable<UpdateClientResponse> {
    const url = `${this.apiBaseUrl}/v1/clients/${clientId}`;
    console.log('✏️ Updating client:', clientId);
    console.log('📋 Update data:', updateData);
    console.log('🔗 API URL:', url);
    return this.http.put<UpdateClientResponse>(url, updateData).pipe(
      tap({
        next: (response) => {
          console.log('✅ Client updated successfully:', response);
          console.log('📋 Response details:', JSON.stringify(response, null, 2));
        },
        error: (error) => {
          console.error('❌ Client update error:', error);
          console.error('Status:', error.status);
          console.error('Status Text:', error.statusText);
          console.error('Error Message:', error.message);
          console.error('Response Body:', error.error);
        }
      })
    );
  }

  /**
   * Generate a unique company code prefixed by the first two letters of the branch
   * @param branchName - The branch name
   * @returns Generated company code
   */
  generateCompanyCode(branchName: string): string {
    let prefix = 'XX';
    if (branchName && typeof branchName === 'string') {
      const lettersOnly = branchName.replace(/[^A-Za-z]/g, '');
      if (lettersOnly.length >= 2) {
        prefix = lettersOnly.slice(0, 2).toUpperCase();
      } else if (lettersOnly.length === 1) {
        prefix = (lettersOnly[0] + 'X').toUpperCase();
      }
    }

    const now = Date.now() % 1000000;
    const rand = Math.floor(Math.random() * 1000);
    const combined = (now + rand) % 1000000;
    const numeric = combined.toString().padStart(6, '0');
    return `${prefix}${numeric}`;
  }

  /**
   * Format a date value into YYYY-MM-DD string (ISO format for API)
   * @param value - Date instance or parseable date string
   * @returns Formatted date string
   */
  formatDateToYYYYMMDD(value: any): string {
    if (!value) return '';
    let date: Date;
    if (value instanceof Date) {
      date = value;
    } else {
      date = new Date(value);
    }
    if (isNaN(date.getTime())) {
      return String(value);
    }
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  /**
   * Format a date value into DD-MM-YYYY string (for display)
   * @param value - Date instance or parseable date string
   * @returns Formatted date string
   */
  formatDateToDDMMYYYY(value: any): string {
    if (!value) return '';
    let date: Date;
    if (value instanceof Date) {
      date = value;
    } else {
      date = new Date(value);
    }
    if (isNaN(date.getTime())) {
      return String(value);
    }
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
}
